import { assign, spawn } from 'xstate';
import { pure, sendParent } from 'xstate/lib/actions';

import {
  Config,
  LogEntriesConstantsEnums,
  LogEntryPriorityEnum,
  LogEntryTypesEnums
} from '@nw/shared/config';
import {
  getAPIErrorEnum,
  useGetLoginWithPhoneOTP,
  useGetSignUpWithPhoneOTP,
  useGetVerifiedUserPhoneOTP,
  useSendOtpToVerifyEmailAPI,
  useUpdateUserProfileAPI,
  useVerifyEmailOTPAPI,
  useVerifyPhoneNumberOTP,
  useVerifySignUpWithPhoneOTP
} from '@nw/shared/data-access';
import {
  BROWSER_SESSION_ID_NOT_FOUND_IN_HEADERS,
  captureLogEntriesExceptionWithScope,
  EMAIL_NOT_REGISTERED,
  INVALID_REQUEST_SIGNATURE,
  PHONE_NUMBER_NOT_REGISTERED,
  PHONE_NUMBER_NOT_VERIFIED
} from '@nw/shared/utils';

import {
  authCoreMachineActions,
  authCoreMachineEvents,
  authCoreMachineGuards,
  authCoreMachineServices
} from '../../constants/authCoreMachineConstants';
import {
  ON_SUCCESS_EMAIL_OTP_VERIFICATION,
  ON_SUCCESS_OTP_VERIFICATION,
  RESEND_OTP_CLICKED
} from '../../constants/otpFieldMachineConstants';
import { authCoreMachine } from '../../machines/authCoreMachine';
import { otpFieldMachine } from '../../machines/OtpFieldMachine';
import {
  AuthCoreMachineContext,
  AuthCoreMachineEvent,
  GoToOTPVerificationEvent
} from '../../types/authCoreMachineTypes';

const actions = authCoreMachineActions;

const services = authCoreMachineServices;
const guards = authCoreMachineGuards;
const events = authCoreMachineEvents;

export const useAuthCoreMachine = () => {
  const params = new URLSearchParams(window.location.search);

  const { verifyPhoneNumberOTPAPI } = useVerifyPhoneNumberOTP();
  const { userLoginWithPhoneNumberAPI } = useGetLoginWithPhoneOTP();
  const { getSignUpWithPhoneOTPAPI } = useGetSignUpWithPhoneOTP();
  const { verifySignUpWithPhoneOTPAPI } = useVerifySignUpWithPhoneOTP();
  const { getVerifiedUserPhoneOTPAPI } = useGetVerifiedUserPhoneOTP();
  const { sendOtpToVerifyEmailAPI } = useSendOtpToVerifyEmailAPI();

  const { updateUserProfileDetailsAPI } = useUpdateUserProfileAPI();

  const { verifyEmailOTPAPI } = useVerifyEmailOTPAPI();

  const getAuthCoreMachineWithConfig = (
    fieldValue: string,
    clientId?: string,
    fieldType?: string,
    shouldStartWithInitialState = false,
    fieldName?: string,
    enableOtpAutofill?: string
  ) => {
    return authCoreMachine(
      shouldStartWithInitialState,
      fieldType,
      fieldValue,
      fieldName
    ).withConfig({
      actions: {
        [actions.doActionsFromParentOnEntryGetOTP]: sendParent(
          (context: AuthCoreMachineContext, event: any) => {
            return {
              type: events.onEntryGetOTP
            };
          }
        ),
        [actions.doActionsFromParentOnEnterGetEmailOTP]: sendParent(
          (context: AuthCoreMachineContext, event: any) => ({
            type: events.onEntryGetEmailOTP
          })
        ),
        [actions.doActionsFromParentOnSuccessGetOTP]: sendParent(
          (context: AuthCoreMachineContext, event: any) => {
            captureLogEntriesExceptionWithScope(
              LogEntriesConstantsEnums.GET_LOGIN_OTP_API_SUCCESS,
              {
                userDetails: { userId: event?.data?.user_id },
                path: window.location.href
              },
              LogEntryTypesEnums.INFORMATION,
              LogEntryPriorityEnum.HIGH
            );

            return {
              type: events.onSuccessGetOTP,
              event: event
            };
          }
        ),
        [actions.doActionsFromParentOnSuccessGetEmailOTP]: sendParent(
          (context: AuthCoreMachineContext, event: any) => ({
            type: events.onSuccessGetEmailOTP,
            event: event
          })
        ),
        [actions.doActionsFromParentOnErrorGetOTP]: sendParent(
          (context: AuthCoreMachineContext, event: any) => {
            return {
              type: events.onErrorGetOTP,
              data: event.data
            };
          }
        ),
        [actions.onErrorGetLoginOTP]: (context, event: any) => {
          captureLogEntriesExceptionWithScope(
            LogEntriesConstantsEnums.GET_LOGIN_OTP_API_FAILED,
            {
              path: window.location.href,
              error: event.data?.message
            },
            LogEntryTypesEnums.INFORMATION,
            LogEntryPriorityEnum.HIGH
          );
        },
        [actions.doActionsFromParentOnErrorGetEmailOTP]: sendParent(
          (context: AuthCoreMachineContext, event: any) => {
            return {
              type: events.onErrorGetEmailOTP,
              data: event.data
            };
          }
        ),
        [actions.doActionsFromParentOnSuccessGetSignUpOTP]: sendParent(
          (context: AuthCoreMachineContext, event: any) => ({
            type: events.onSuccessGetSignUpOTP,
            event: event
          })
        ),
        [actions.onInvalidRequestSignatureError]: (context, event) => {
          window.location.reload();
        },
        [actions.setAccessTokenAndRefreshTokenToContext]: assign(
          (context, event: any) => {
            return {
              accessToken: event.data.accessToken,
              refreshToken: event.data.refreshToken
            };
          }
        ),
        [actions.setIsOtpSentToUserTrueInContext]: assign((context, event) => {
          return {
            isOtpSentToUser: true
          };
        }),

        [actions.setIsGetOtpButtonLoadingToTrue]: assign((context, event) => {
          return {
            isGetOtpButtonLoading: true
          };
        }),

        [actions.setIsGetOtpButtonLoadingToFalse]: assign((context, event) => {
          return {
            isGetOtpButtonLoading: false
          };
        }),

        [actions.spawnExistingUserOTPFieldMachine]: assign(
          (parentMachineContext: any) => {
            const otpFieldMachineWithConfig = otpFieldMachine().withConfig({
              actions: {
                setAccessTokenAndRefreshTokenAndSendParent: pure(
                  (context, event: any) => {
                    if (fieldType === 'email') {
                      return sendParent({
                        type: ON_SUCCESS_EMAIL_OTP_VERIFICATION
                      });
                    } else
                      return sendParent({
                        type: ON_SUCCESS_OTP_VERIFICATION,
                        data: {
                          accessToken: event.data.access_token,
                          refreshToken: event.data.refresh_token
                        }
                      });
                  }
                ),
                [actions.onClickResendOtp]: pure((context, event: any) => {
                  return sendParent({
                    type: RESEND_OTP_CLICKED
                  });
                }),
                hideResendAndTimerField: assign((context, event) => ({
                  shouldShowResendAndTimerField: false
                })),
                sendMultiStepFormNextToContext: assign(
                  (context, event: any) => ({
                    next: event.next,
                    back: event.back
                  })
                ),
                callNextFromContext: (context: any, event) => {
                  context.next && context.next();
                },
                callBackFromContext: (context: any, event) => {
                  context.back && context.back();
                }
              },
              services: {
                verifyOTP: (context, event: any) => {
                  if (fieldType && fieldType === 'email') {
                    return verifyEmailOTPAPI({
                      otp: event.values.otp
                    });
                  } else
                    return verifyPhoneNumberOTPAPI({
                      phoneNumber: fieldValue,
                      otpValue: event.values.otp,
                      countryCode: '+91',
                      clientId: Config['IB_USER_ACCOUNTS_FRONTEND_CLIENT_ID'],
                      acceptedTandC: event.values.acceptTnC
                        ? event.values.acceptTnC
                        : true
                    });
                },

                resendOTP: (context, event) => {
                  if (fieldType && fieldType === 'email') {
                    return sendOtpToVerifyEmailAPI({
                      email: fieldValue
                    });
                  } else
                    return userLoginWithPhoneNumberAPI({
                      phoneNumber: fieldValue,
                      clientId: clientId,
                      domain: enableOtpAutofill
                        ? window.location.host
                        : undefined
                    });
                }
              }
            });
            return {
              otpFieldRef: spawn(otpFieldMachineWithConfig)
            };
          }
        ),
        [actions.spawnNewUserOTPFieldMachine]: assign(
          (parentMachineContext: any) => {
            const otpFieldMachineWithConfig = otpFieldMachine().withConfig({
              actions: {
                setAccessTokenAndRefreshTokenAndSendParent: pure(
                  (context, event: any) => {
                    return sendParent({
                      type: ON_SUCCESS_OTP_VERIFICATION,
                      data: {
                        accessToken: event.data.access_token,
                        refreshToken: event.data.refresh_token
                      }
                    });
                  }
                ),
                [actions.onClickResendOtp]: pure((context, event: any) => {
                  return sendParent({
                    type: RESEND_OTP_CLICKED
                  });
                }),
                hideResendAndTimerField: assign((context: any, event: any) => ({
                  shouldShowResendAndTimerField: false
                })),
                sendMultiStepFormNextToContext: assign(
                  (context, event: any) => ({
                    next: event.next,
                    back: event.back
                  })
                ),
                callNextFromContext: (context: any, event) => {
                  context.next && context.next();
                },
                callBackFromContext: (context: any, event) => {
                  context.back && context.back();
                }
              },
              services: {
                verifyOTP: (context, event: any) => {
                  const customID = params.get('custom_id');
                  const requestObject = {
                    phoneNumber: fieldValue,
                    countryCode: '+91',
                    otpValue: event.values.otp,
                    clientId: Config['IB_USER_ACCOUNTS_FRONTEND_CLIENT_ID'],
                    acceptedTandC: event.values.acceptTnC
                      ? event.values.acceptTnC
                      : true,
                    customId: customID ? customID : undefined
                  };

                  return verifySignUpWithPhoneOTPAPI(requestObject);
                },
                resendOTP: (context, event) => {
                  return getVerifiedUserPhoneOTPAPI({
                    phoneNumber: fieldValue,
                    countryCode: '+91',
                    clientId: clientId,
                    domain: enableOtpAutofill ? window.location.host : undefined
                  });
                }
              }
            });
            return {
              otpFieldRef: spawn(otpFieldMachineWithConfig)
            };
          }
        )
      },
      services: {
        [services.getLoginOtp]: (
          context: AuthCoreMachineContext,
          event: AuthCoreMachineEvent
        ) => {
          event = event as GoToOTPVerificationEvent;
          //TODO:add error handling for phone number no present
          const result = userLoginWithPhoneNumberAPI({
            phoneNumber: fieldValue,
            clientId: clientId,
            domain: enableOtpAutofill ? window.location.host : undefined
          });
          return result;
        },
        [services.getSignUpOTP]: (context, event) =>
          getSignUpWithPhoneOTPAPI({
            phoneNumber: fieldValue,
            countryCode: '+91',
            clientId: clientId,
            domain: enableOtpAutofill ? window.location.host : undefined
          }),
        [services.getVerifiedUserOTP]: (context, event) =>
          getVerifiedUserPhoneOTPAPI({
            phoneNumber: fieldValue,
            countryCode: '+91',
            clientId: clientId,
            domain: enableOtpAutofill ? window.location.host : undefined
          }),
        [services.getEmailOtp]: (context, event) => {
          //TODO:add error handling if email not present
          return sendOtpToVerifyEmailAPI({
            email: fieldValue
          });
        },
        [services.updateUserEmail]: (context, event: any) => {
          return updateUserProfileDetailsAPI({
            email: fieldValue
          });
        }
      },
      guards: {
        [guards.isNewUserError]: (context, event: any) => {
          return (
            getAPIErrorEnum(event.data) === PHONE_NUMBER_NOT_REGISTERED ||
            getAPIErrorEnum(event.data) === EMAIL_NOT_REGISTERED
          );
        },
        [guards.isPhoneNumberNotVerifiedError]: (context, event: any) => {
          return getAPIErrorEnum(event.data) === PHONE_NUMBER_NOT_VERIFIED;
        },
        [guards.isInvalidRequestSignatureError]: (context, event: any) => {
          const errorEnum = getAPIErrorEnum(event.data);
          return (
            errorEnum === INVALID_REQUEST_SIGNATURE ||
            errorEnum === BROWSER_SESSION_ID_NOT_FOUND_IN_HEADERS
          );
        }
      }
    });
  };

  return { getAuthCoreMachineWithConfig };
};
